import React from 'react';
import { PlayingCard } from './PlayingCard';
import { Hand as HandType } from '../types';
import { getHandValue, isBlackjack } from '../utils/deck';

interface HandProps {
  hand: HandType;
  label: string;
  showValue?: boolean;
  showResult?: boolean;
  isActive?: boolean; // Whether this hand is currently being played
  handIndex?: number; // Hand number for split hands
  totalHands?: number; // Total number of hands
}

export const Hand: React.FC<HandProps> = ({
  hand,
  label,
  showValue = true,
  showResult = true,
  isActive = true,
  handIndex,
  totalHands = 1
}) => {
  const visibleCards = hand.cards.filter(c => c.faceUp);
  const { value, soft } = getHandValue(visibleCards);
  // Only show blackjack if all cards are visible (both face up)
  const allCardsVisible = hand.cards.length > 0 && hand.cards.every(c => c.faceUp);
  const hasBlackjack = allCardsVisible && isBlackjack(hand.cards) && hand.cards.length === 2;

  const getResultColor = () => {
    if (!hand.result) return '';
    switch (hand.result) {
      case 'win':
      case 'blackjack':
        return 'text-green-400';
      case 'loss':
        return 'text-red-400';
      case 'push':
        return 'text-yellow-400';
    }
  };

  const getResultText = () => {
    if (!hand.result) return '';
    switch (hand.result) {
      case 'win':
        return 'WIN!';
      case 'blackjack':
        return 'BLACKJACK!';
      case 'loss':
        return 'LOSE';
      case 'push':
        return 'PUSH';
    }
  };

  const displayLabel = totalHands > 1 && handIndex !== undefined
    ? `${label} (Hand ${handIndex + 1})`
    : label;

  return (
    <div className={`flex flex-col items-center space-y-2 sm:space-y-4 ${totalHands > 1 ? 'p-2 rounded-lg' : ''
      } ${isActive && totalHands > 1 ? 'ring-2 ring-yellow-400 bg-yellow-400/10' : ''}`}>
      <div className="text-white font-semibold text-sm sm:text-base md:text-lg flex items-center gap-2">
        {displayLabel}
        {isActive && totalHands > 1 && (
          <span className="text-xs bg-yellow-500 text-black px-2 py-0.5 rounded">Playing</span>
        )}
      </div>

      <div className="flex justify-center items-center min-h-[140px] pl-8 sm:pl-12">
        {hand.cards.map((card, index) => (
          <div
            key={index}
            className="transform transition-all duration-500 hover:-translate-y-4 hover:z-10 -ml-6 sm:-ml-8 relative"
            style={{ zIndex: index }}
          >
            <PlayingCard card={card} index={index} />
          </div>
        ))}
      </div>

      {/* Hand Value - only show if allowed */}
      {(showValue || value > 21 || hasBlackjack) && hand.cards.length > 0 && (
        <div className="text-center">
          <div className="text-white font-bold text-base sm:text-lg md:text-xl">
            {value > 21 ? (
              <span className="text-red-400">BUST{showValue ? ` (${value})` : ''}</span>
            ) : hasBlackjack ? (
              <span className="text-yellow-400">BLACKJACK!</span>
            ) : showValue ? (
              <>
                {soft && <span className="text-yellow-300">Soft </span>}
                {value}
              </>
            ) : null}
          </div>
        </div>
      )}

      {/* Hand Result - always show when present (except duplicate blackjack banner) */}
      {hand.result && hand.result !== 'blackjack' && showResult && (
        <div className={`text-xl sm:text-2xl md:text-3xl font-bold mt-2 animate-pulse-slow fade-in-up ${getResultColor()} text-center`}>
          {getResultText()}
        </div>
      )}
    </div>
  );
};

// Component to display multiple hands (for split scenarios)
interface MultiHandProps {
  hands: HandType[];
  currentHandIndex: number;
  label: string;
  showValue?: boolean;
  showResult?: boolean;
  isPlaying?: boolean;
}

export const MultiHand: React.FC<MultiHandProps> = ({
  hands,
  currentHandIndex,
  label,
  showValue = true,
  showResult = true,
  isPlaying = false
}) => {
  if (hands.length === 1) {
    return (
      <Hand
        hand={hands[0]}
        label={label}
        showValue={showValue}
        showResult={showResult}
      />
    );
  }

  return (
    <div className="flex flex-wrap justify-center gap-4">
      {hands.map((hand, index) => (
        <Hand
          key={index}
          hand={hand}
          label={label}
          showValue={showValue}
          showResult={showResult}
          isActive={isPlaying && index === currentHandIndex}
          handIndex={index}
          totalHands={hands.length}
        />
      ))}
    </div>
  );
};
